import React, { FC, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useSnackbar, AppSectionHeader } from '../../../components-v2/shared';
import useStore from '../../../store/useStore';
import { AddEmailForm } from './AddEmailForm';
import { BlockedEmailsList } from './BlockedEmailsList';
import './spam-management-section.scss';

export interface SpamManagementSectionProps {
    companyId: number;
    formIsDisabled?: boolean;
}

export const SpamManagementSection: FC<SpamManagementSectionProps> = observer(({ companyId, formIsDisabled }) => {
    const { blocklistStore } = useStore();
    const { addSnackbar } = useSnackbar();

    useEffect(() => {
        if (companyId) {
            blocklistStore.fetchBlocklist(companyId);
        }

        return () => {
            blocklistStore.reset();
        };
    }, [companyId, blocklistStore]);

    const handleAddEmail = async (email: string): Promise<boolean> => {
        const result = await blocklistStore.addEmail(companyId, email);

        if (result) {
            addSnackbar('Email added to blocklist successfully', {
                variant: 'success'
            });
        } else {
            addSnackbar(blocklistStore.error || 'Failed to add email to blocklist', {
                variant: 'error'
            });
        }

        return result;
    };

    const handleDeleteEmail = async (id: number): Promise<boolean> => {
        const result = await blocklistStore.deleteEmail(companyId, id);

        if (result) {
            addSnackbar('Email removed from blocklist successfully', {
                variant: 'success'
            });
        } else {
            addSnackbar(blocklistStore.error || 'Failed to remove email from blocklist', {
                variant: 'error'
            });
        }

        return result;
    };

    const handlePageChange = (page: number) => {
        blocklistStore.fetchBlocklist(companyId, page);
    };

    return (
        <div className="spam-management-section">
            <AppSectionHeader title="Spam Management" subtitle="Block email addresses to prevent spam leads" />

            <AddEmailForm onAdd={handleAddEmail} loading={blocklistStore.loading} disabled={formIsDisabled} />

            <BlockedEmailsList
                entries={blocklistStore.blocklistEntries}
                totalCount={blocklistStore.totalCount}
                currentPage={blocklistStore.currentPage}
                pageSize={blocklistStore.pageSize}
                onDelete={handleDeleteEmail}
                onPageChange={handlePageChange}
                loading={blocklistStore.loading}
                disabled={formIsDisabled}
            />
        </div>
    );
});
